import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { AuthLayout } from '../layouts/AuthLayout';
import { Button, Field, Notice } from '../components/ui';
import { ApiError } from '../lib/api';
import { clearPending, readPending, registerSchool } from '../lib/register';
import { api } from '../lib/api';
import { CONTACT_URL } from '../lib/signup';
import { useQuery } from '@tanstack/react-query';
import { JOB_TITLES, getRoleOptions, saveJobTitle } from '../lib/onboarding';
import { Choice } from './Welcome';
import { clearPendingInvite, getInvite, readPendingInvite, registerWithInvite, ROLE_WORD } from '../lib/invites';
import { useMe, useSession } from '../lib/session';
import { getProfile, settingOptions } from '../lib/onboarding';
import { GetTheApp } from '../components/GetTheApp';
import './Gates.css';

/** Teachers use the phone app; the web is for leaders in this first release. */
export function TeacherGate() {
  const { signOut } = useSession();
  return (
    <AuthLayout
      title="Your lessons live in the app"
      subtitle="The EducAitor web app is for leaders at the moment. Record lessons and read your feedback in the EducAitor app on your phone."
    >
      <Button variant="secondary" size="lg" block onClick={() => signOut()}>
        Sign out
      </Button>
    </AuthLayout>
  );
}

/**
 * Someone with their own personal account, set up: lessons are recorded and
 * read in the phone app, so the web says they're ready, where to get the app,
 * and what their feedback is shaped by. (A fuller web view for individuals,
 * "My EducAitor", is the next phase.)
 */
export function PersonalGate() {
  const me = useMe();
  const { signOut } = useSession();
  const profile = useQuery({ queryKey: ['schoolProfile'], queryFn: getProfile });
  const p = profile.data;
  const setting = p ? (p.phase === 'other' ? p.phaseOther : settingOptions(p).find((x) => x.value === p.phase)?.label ?? null) : null;
  const rows = [
    ['Setting', setting],
    ['Country', p?.country ?? null],
    ['Where you work', p?.workplace ?? null],
    ['Working on', p?.priorities?.length ? p.priorities.join(', ') : null],
  ].filter(([, v]) => !!v) as [string, string][];
  return (
    <AuthLayout
      personal
      title={`You\u2019re all set${me.name ? `, ${me.name.split(' ')[0]}` : ''}`}
      subtitle="Record lessons and read your feedback in the EducAitor app on your phone. Sign in there with this email."
    >
      <GetTheApp email={me.email} />
      {rows.length ? (
        <dl className="personal-answers">
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt className="muted">{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <p className="muted">
        To change these or anything else about your account,{' '}
        <a href={CONTACT_URL} target="_blank" rel="noreferrer">
          get in touch
        </a>
        .
      </p>
      <Button variant="ghost" size="lg" block onClick={() => signOut()}>
        Sign out
      </Button>
    </AuthLayout>
  );
}

/**
 * Signed in to the account system, but the EducAitor profile was never
 * finished (sign-up was interrupted, or a teacher who hasn't joined in the
 * app). Leaders can finish creating their school here.
 */
export function UnregisteredGate() {
  const { signOut, refresh } = useSession();
  const navigate = useNavigate();
  const pending = readPending();
  const [name, setName] = useState(pending?.name ?? '');
  useEffect(() => {
    if (name) return;
    fetchUserAttributes()
      .then((a) => setName(a.name ?? [a.given_name, a.family_name].filter(Boolean).join(' ')))
      .catch(() => {});
    // Only on first render: don't overwrite what the person types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [schoolName, setSchoolName] = useState(pending?.schoolName ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Signed up with Google or Apple from an invite: join that school instead.
  const [inviteToken, setInviteToken] = useState(() => readPendingInvite());
  const invite = useQuery({
    queryKey: ['invite', inviteToken],
    queryFn: () => getInvite(inviteToken!),
    enabled: !!inviteToken,
    retry: false,
  });
  const openInvite = invite.data?.status === 'open' ? invite.data : null;

  const join = async (e: FormEvent) => {
    e.preventDefault();
    if (!inviteToken || !name.trim()) {
      setError('Enter your name.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const attrs = await fetchUserAttributes();
      await registerWithInvite(attrs.email ?? '', name, inviteToken);
      clearPendingInvite();
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn\u2019t work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const finish = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !schoolName.trim()) {
      setError('Enter your name and your organisation\u2019s name.');
      return;
    }
    setBusy(true);
    try {
      const attrs = await fetchUserAttributes();
      await registerSchool(attrs.email ?? '', name, schoolName);
      clearPending();
      await refresh();
      navigate('/welcome', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn\u2019t work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (openInvite) {
    return (
      <AuthLayout
        title={`Join ${openInvite.schoolName}`}
        subtitle={`${openInvite.inviterName ?? 'Your organisation'} invited ${openInvite.email} as ${ROLE_WORD[openInvite.role]}. Your sign-in is ready.`}
      >
        <form className="auth-form" onSubmit={join} noValidate>
          {error ? <Notice tone="error">{error}</Notice> : null}
          <Field id="join-name" label="Your name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <Button type="submit" size="lg" block loading={busy}>
            Join {openInvite.schoolName}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              clearPendingInvite();
              setInviteToken(null);
            }}
          >
            Set up a new organisation instead
          </Button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Finish setting up your organisation" subtitle="Your sign-in is ready. Tell us who you are and which organisation this is for.">
      <form className="auth-form" onSubmit={finish} noValidate>
        {error ? <Notice tone="error">{error}</Notice> : null}
        <Field id="finish-name" label="Your name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Field id="finish-school" label="Organisation name" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} />
        <Button type="submit" size="lg" block loading={busy}>
          Create organisation
        </Button>
        <Button type="button" variant="ghost" onClick={() => signOut()}>
          Sign out
        </Button>
      </form>
      <p className="auth-foot">Joining your team? Use the EducAitor app with your organisation code, or the button in your invite email.</p>
    </AuthLayout>
  );
}

export function NotFoundPage() {
  return (
    <div className="page">
      <h1>Page not found</h1>
      <p className="muted">That page doesn&rsquo;t exist. Use the menu to find your way back.</p>
    </div>
  );
}

/**
 * A leader in a school that's already set up, who hasn't told us their role
 * (e.g. promoted or invited as a leader). Just that one question, not the
 * whole school setup again.
 */
export function RoleGate() {
  const me = useMe();
  const { refresh } = useSession();
  const roles = useQuery({ queryKey: ['roleOptions'], queryFn: getRoleOptions });
  const options = roles.data ?? JOB_TITLES;
  const [role, setRole] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const value = role === 'Other' ? other.trim() : role;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!value) return;
    setError(null);
    setBusy(true);
    try {
      await saveJobTitle(value);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn\u2019t save. Try again.');
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={`What\u2019s your role at ${me.school_name ?? 'your school'}?`}
      subtitle="So we send the right summaries to the right people. You can change it later in Your account."
    >
      <form onSubmit={save} noValidate style={{ display: 'grid', gap: 16 }}>
        {error ? <Notice tone="error">{error}</Notice> : null}
        <Choice label="Your role" options={[...options, 'Other'].map((t) => ({ value: t, label: t }))} value={role} onChange={setRole} stacked />
        {role === 'Other' ? <Field id="role-other" label="Your job title" value={other} onChange={(e) => setOther(e.target.value)} autoFocus /> : null}
        <Button type="submit" size="lg" block loading={busy} disabled={!value}>
          Continue
        </Button>
      </form>
    </AuthLayout>
  );
}

/**
 * A leader who isn't an owner, in a school whose setup isn't finished. Only
 * an owner can set the school up (they agree the terms for it), so wait.
 */
export function SetupPendingGate() {
  const me = useMe();
  const { refresh, signOut } = useSession();
  const [checking, setChecking] = useState(false);
  const owners = me.school_owners ?? [];
  const who = owners.length ? owners.join(' or ') : 'an owner of your organisation';
  return (
    <AuthLayout
      title={`${me.school_name ?? 'Your organisation'} is being set up`}
      subtitle={`${who} needs to finish setting up the organisation on the web before you can use it here. You can keep recording lessons in the app meanwhile.`}
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <Button
          size="lg"
          block
          loading={checking}
          onClick={async () => {
            setChecking(true);
            await refresh();
            setChecking(false);
          }}
        >
          Check again
        </Button>
        <Button variant="secondary" size="lg" block onClick={() => signOut()}>
          Sign out
        </Button>
      </div>
    </AuthLayout>
  );
}

/**
 * An EducAitor admin has paused the organisation: nothing is deleted, and
 * everything is back as it was once it's resumed.
 */
export function PausedGate() {
  const me = useMe();
  const { refresh, signOut } = useSession();
  const [checking, setChecking] = useState(false);
  const org = me.school_name ?? 'Your organisation';
  return (
    <AuthLayout
      title={`${org}’s account is paused`}
      subtitle="Your lessons, reports and settings are all kept, and everything will be here when the account is active again. Someone who manages EducAitor at your organisation can tell you more."
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <Button
          size="lg"
          block
          loading={checking}
          onClick={async () => {
            setChecking(true);
            await refresh();
            setChecking(false);
          }}
        >
          Check again
        </Button>
        <Button variant="secondary" size="lg" block onClick={() => signOut()}>
          Sign out
        </Button>
      </div>
    </AuthLayout>
  );
}

/** An invite link (or its token) pasted where a code goes. */
const INVITE_TOKEN = /(?:\/join\/|invite=)?([A-Za-z0-9_-]{43})(?:[/?#]|$)/;

/**
 * An account with no organisation: removed by a leader or an EducAitor admin,
 * declined, or waiting for a leader to approve them. Removed and declined
 * people can join another organisation with its code or their invite link
 * (the API only lets them see, join with or delete their account), and
 * everyone can delete their account.
 */
export function RemovedGate() {
  const me = useMe();
  const { refresh, signOut } = useSession();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [deleting, setDeleting] = useState<'ask' | 'busy' | null>(null);
  const [joining, setJoining] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const org = me.pending_school_name ?? 'your organisation';
  const waiting = me.join_status === 'pending';

  const remove = async () => {
    setError(null);
    setDeleting('busy');
    try {
      await api('/users/me', { method: 'DELETE' });
      try {
        await signOut();
      } catch {
        // The sign-in account is already gone; nothing left to sign out of.
      }
      navigate('/sign-in', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
      setDeleting('ask');
    }
  };

  const join = async (e: FormEvent) => {
    e.preventDefault();
    const typed = code.trim();
    if (!typed) {
      setError('Enter your organisation’s code or paste your invite link.');
      return;
    }
    setError(null);
    setJoining(true);
    try {
      const token = typed.length >= 43 ? typed.match(INVITE_TOKEN)?.[1] : undefined;
      await api('/users/me/join', { method: 'POST', body: JSON.stringify(token ? { inviteToken: token } : { code: typed }) });
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
      setJoining(false);
    }
  };

  const none = me.join_status === 'none';
  const title = waiting
    ? `Waiting for ${org}`
    : me.join_status === 'declined'
      ? 'Your request wasn’t approved'
      : none
        ? 'Join your organisation'
        : `You’re no longer part of ${org}`;
  const subtitle = waiting
    ? `A leader at ${org} needs to approve you. You’ll get a notification in the EducAitor app when you’re in.`
    : me.join_status === 'declined'
      ? 'If you think this is a mistake, ask a leader there to invite you by email, or join another organisation below.'
      : none
        ? 'EducAitor is used through your school or organisation. Enter its code, or paste the invite link from your email. To use it on your own, get in touch.'
        : 'Your account and your lessons are kept, and you can still read your lessons and reports in the EducAitor app. Joining a new organisation? Enter its code or your invite link. To carry on on your own, get in touch and we’ll move you to an individual account.';

  return (
    <AuthLayout title={title} subtitle={subtitle}>
      <div style={{ display: 'grid', gap: 12 }}>
        {error ? <Notice tone="error">{error}</Notice> : null}
        {deleting ? (
          <>
            <Notice tone="error">This deletes your account, lessons, reports and messages for good. It can’t be undone.</Notice>
            <Button variant="danger" size="lg" block loading={deleting === 'busy'} onClick={remove}>
              Delete my account
            </Button>
            <Button variant="ghost" block onClick={() => setDeleting(null)} disabled={deleting === 'busy'}>
              Keep my account
            </Button>
          </>
        ) : (
          <>
            {waiting ? null : (
              <form className="auth-form" onSubmit={join} noValidate>
                <Field
                  id="join-code"
                  label="Organisation code or invite link"
                  placeholder="EDU-…"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoComplete="off"
                />
                <Button type="submit" size="lg" block loading={joining}>
                  Join organisation
                </Button>
              </form>
            )}
            <Button
              variant={waiting ? 'primary' : 'secondary'}
              size="lg"
              block
              loading={checking}
              onClick={async () => {
                setChecking(true);
                await refresh();
                setChecking(false);
              }}
            >
              Check again
            </Button>
            {waiting ? null : (
              <Button variant="secondary" size="lg" block onClick={() => window.open(CONTACT_URL, '_blank', 'noopener')}>
                Contact us
              </Button>
            )}
            <Button variant="ghost" block onClick={() => signOut()}>
              Sign out
            </Button>
            <Button variant="ghost" block onClick={() => setDeleting('ask')}>
              Delete my account
            </Button>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
